<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { getApiUrl } from '../api';

  export let showModal = false;
  export let preSelectedBanks = []; // CORRECTED PROP NAME

  const dispatch = createEventDispatcher();

  let searchTerm = '';
  let searchResults = [];
  let selectedFromList = {};
  let customBank = '';
  let customBanks = [];
  let allBanks = [];
  let isLoading = true;

  // This function is called when the modal opens
  async function initialize() {
    isLoading = true;
    
    // Reset selections and populate from the parent prop
    selectedFromList = {};
    customBanks = [];

    try {
      const response = await fetch(getApiUrl('/banks'));
      if (!response.ok) throw new Error('Failed to load bank list.');
      allBanks = await response.json();
      searchResults = allBanks;

      const masterBankNames = new Set(allBanks.map(b => b.name));
      
      // Separate the pre-selected banks into master and custom lists
      preSelectedBanks.forEach(bankName => {
        if (masterBankNames.has(bankName)) {
          selectedFromList[bankName] = true;
        } else {
          if (!customBanks.includes(bankName)) {
            customBanks.push(bankName);
          }
        }
      });
      
    } catch (err) {
      console.error(err);
    } finally {
      isLoading = false;
    }
  }

  $: if (searchTerm) {
    searchResults = allBanks.filter(bank => 
      bank.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  } else {
    searchResults = allBanks;
  }

  function addCustomBank() {
    if (customBank.trim() && !customBanks.includes(customBank.trim())) {
      customBanks = [...customBanks, customBank.trim()];
      customBank = '';
    }
  }

  function removeCustomBank(bankToRemove) {
    customBanks = customBanks.filter(b => b !== bankToRemove);
  }

  function closeModal() {
    dispatch('close');
  }

  function confirmSelection() {
    const listSelection = Object.keys(selectedFromList).filter(key => selectedFromList[key]);
    const finalSelection = [...new Set([...listSelection, ...customBanks])];
    dispatch('confirm', { selected: finalSelection });
  }

  $: if (showModal) {
    initialize();
  }
</script>

{#if showModal}
  <div class="modal-backdrop" on:click={closeModal}>
    <div class="modal-content" on:click|stopPropagation>
      <div class="modal-header">
        <h3>Select Participating Banks</h3>
        <button class="close-btn" on:click={closeModal}>&times;</button>
      </div>
      <div class="modal-body">
        <div class="search-section">
          <input type="text" bind:value={searchTerm} placeholder="Search for a bank..." />
        </div>
        <div class="bank-list">
          {#if isLoading}
            <p>Loading banks...</p>
          {:else if searchResults.length > 0}
            {#each searchResults as bank (bank._id)}
              <div class="bank-item">
                <input type="checkbox" id={bank._id} bind:checked={selectedFromList[bank.name]} />
                <label for={bank._id}>{bank.name} ({bank.country})</label>
              </div>
            {/each}
          {:else}
            <p>No matching banks found in the master list.</p>
          {/if}
        </div>
        <div class="custom-add-section">
          <h4>Add a Custom Bank</h4>
          <p>If a bank isn't in the list, add it manually here.</p>
          <div class="custom-input-group">
            <input type="text" bind:value={customBank} placeholder="Enter custom bank name..." on:keydown={(e) => e.key === 'Enter' && addCustomBank()} />
            <button on:click={addCustomBank} type="button">Add</button>
          </div>
          <div class="custom-bank-tags">
            {#each customBanks as bank}
              <span class="tag">
                {bank}
                <button on:click={() => removeCustomBank(bank)}>&times;</button>
              </span>
            {/each}
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn-secondary" on:click={closeModal}>Cancel</button>
        <button class="btn-primary" on:click={confirmSelection}>Confirm Selection</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed; top: 0; left: 0;
    width: 100%; height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex; justify-content: center; align-items: center;
    z-index: 1000;
  }
  .modal-content {
    background: white; border-radius: 8px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.3);
    width: 90%; max-width: 600px; max-height: 90vh;
    display: flex; flex-direction: column;
  }
  .modal-header {
    padding: 1rem 1.5rem; border-bottom: 1px solid #dee2e6;
    display: flex; justify-content: space-between; align-items: center;
  }
  .modal-header h3 { margin: 0; }
  .close-btn { background: none; border: none; font-size: 1.5rem; cursor: pointer; }
  .modal-body { padding: 1.5rem; overflow-y: auto; }
  .bank-list {
    max-height: 200px; overflow-y: auto;
    border: 1px solid #ccc; padding: 1rem; border-radius: 4px;
    margin-bottom: 1.5rem;
  }
  .bank-item { display: flex; align-items: center; gap: 10px; padding: 5px 0; }
  input[type="text"] {
    width: 100%; padding: 0.5rem;
    box-sizing: border-box; margin-bottom: 1rem;
  }
  .custom-add-section { border-top: 1px solid #dee2e6; padding-top: 1rem; }
  .custom-input-group { display: flex; gap: 10px; }
  .custom-input-group input { margin-bottom: 0; }
  .custom-bank-tags { margin-top: 1rem; display: flex; flex-wrap: wrap; gap: 10px; }
  .tag {
    background-color: #e9ecef; padding: 5px 10px; border-radius: 15px;
    display: flex; align-items: center; gap: 8px;
  }
  .tag button { background: none; border: none; cursor: pointer; font-size: 1rem; padding: 0; line-height: 1; }
  .modal-footer {
    padding: 1rem 1.5rem; border-top: 1px solid #dee2e6;
    display: flex; justify-content: flex-end; gap: 1rem;
  }
  .btn-primary, .btn-secondary {
    padding: 0.5rem 1rem; border-radius: 4px;
    border: 1px solid transparent; cursor: pointer; font-size: 1rem;
  }
  .btn-primary { background-color: #007bff; color: white; }
  .btn-secondary { background-color: #6c757d; color: white; }
</style>
